// Auth uses Bearer tokens in localStorage (key 'lp_token'/'lp_user'), same as
// the legacy frontend. We read the stored user immediately to avoid a flash,
// then verify with /api/me. On 401, api.js clears the token + redirects to login.
import { useEffect, useRef, useState } from 'react';
import { ACTIVE_COMPANY_KEY, api, getToken } from '../api.js';
import { loginHrefForCurrentLocation } from '../invite-handoff.js';
import { normalizeTenantProductPolicy } from '../product-policy.js';
import { registrationHandoff } from '../registration-handoff.js';
import { companyOnboardingEntry, normalizeCompanyOnboardingContinuation } from '../../../server/public/company-onboarding-state.js';

function storedUser() {
  try { return JSON.parse(localStorage.getItem('lp_user') || 'null'); } catch { return null; }
}

function storedActiveCompanyId() {
  try { return localStorage.getItem(ACTIVE_COMPANY_KEY) || null; } catch { return null; }
}

function isAcceptInviteRoute() {
  return String(window.location.hash || '').replace(/^#\/?/, '').split('?')[0] === 'accept-invite';
}

export function allowsSessionOnboardingContinuation(location = {}) {
  const route = String(location.hash || '').replace(/^#\/?/, '').split(/[/?]/)[0];
  return !['accept-invite', 'platform'].includes(route) && location.pathname !== '/meta/callback';
}

function continuationContextError() {
  const error = new Error('The saved company setup could not be verified');
  error.code = 'company_onboarding_continuation_unavailable';
  error.status = 503;
  return error;
}

export async function resolveSessionOnboardingContinuation(response, {
  activeCompanyId = null,
  allowed = true,
  request = api.get,
  isCurrent = () => true,
} = {}) {
  if (!isCurrent()) return null;
  if (activeCompanyId || !allowed || response?.auth?.type !== 'supabase' || !response?.activeCompany?.id) return response;
  const result = await request('/api/saas/onboarding/continuation');
  if (!isCurrent()) return null;
  let continuation;
  try { continuation = normalizeCompanyOnboardingContinuation(result); }
  catch { throw continuationContextError(); }
  if (!continuation.companyId) return response;
  const companyId = continuation.companyId;
  const exact = await request('/api/me', { headers: { 'x-company-id': companyId } });
  if (!isCurrent()) return null;
  if (exact?.auth?.type !== 'supabase' || !response.user?.id || exact.user?.id !== response.user.id
    || exact.activeCompany?.id !== companyId || exact.activeCompany.status !== 'active'
    || (exact.tenant && exact.tenant.companyId !== companyId)
    || !['owner', 'admin', 'marketer', 'viewer'].includes(exact.role)
    || !Array.isArray(exact.memberships) || !exact.memberships.some(membership => (
      membership?.companyId === companyId && membership.status === 'active' && membership.role === exact.role
      && (!membership.company || (membership.company.id === companyId && membership.company.status === 'active'))
    ))) throw continuationContextError();
  return exact;
}

function isNoMembershipError(err) {
  return String(err?.message || '').includes('no_company_membership');
}

function isUnauthorizedError(err) {
  return Number(err?.status) === 401 || err?.code === 'unauthorized';
}

function isSafePlatformContextError(err) {
  return err?.status === 403 || err?.status === 404 || err?.status === 503;
}

async function loadPlatformContext(user, { force = false } = {}) {
  if (!force && user?.auth?.type !== 'supabase') return { admin: false };
  if (!force && !user.id && !user.email) return { admin: false };
  try {
    const response = await api.get('/api/saas/platform/context');
    return response.platform || { admin: false };
  } catch (err) {
    if (isSafePlatformContextError(err)) return { admin: false };
    throw err;
  }
}

export async function loadTenantProductPolicy(user, { request = api.get } = {}) {
  if (user?.auth?.type !== 'supabase') return null;
  const companyId = user?.activeCompany?.id || user?.tenant?.companyId || null;
  if (!companyId) return null;

  const response = await request('/api/saas/policy', {
    headers: { 'x-company-id': companyId },
  });
  const policy = normalizeTenantProductPolicy(response?.product);
  if (!policy) {
    const error = new Error('The tenant product policy could not be verified');
    error.code = 'invalid_product_policy';
    error.status = 503;
    throw error;
  }
  return policy;
}

export async function verifyTenantContextPolicy({
  user,
  activeCompanyId,
  requestId,
  isCurrentRequest,
  requestPolicy = loadTenantProductPolicy,
  persist = (verifiedUser) => {
    localStorage.setItem('lp_user', JSON.stringify(verifiedUser));
    if (activeCompanyId) localStorage.setItem(ACTIVE_COMPANY_KEY, activeCompanyId);
  },
} = {}) {
  try {
    const productPolicy = await requestPolicy(user);
    if (!isCurrentRequest(requestId)) return { stale: true, user: null };
    const verifiedUser = { ...user, productPolicy };
    try { persist(verifiedUser); } catch {}
    return { stale: false, user: verifiedUser };
  } catch (error) {
    if (!isCurrentRequest(requestId)) return { stale: true, user: null };
    throw error;
  }
}

export async function resolveTenantContextActivation({
  currentUser,
  nextContext = {},
  requestId,
  isCurrentRequest,
  requestPolicy = loadTenantProductPolicy,
  persist,
} = {}) {
  if (!currentUser) {
    return { activated: false, stale: false, user: null, errorCode: 'missing_session' };
  }
  const activeCompanyId = String(nextContext.activeCompany?.id || '');
  const tenantCompanyId = String(nextContext.tenant?.companyId || '');
  const contextComplete = Boolean(
    activeCompanyId
    && tenantCompanyId === activeCompanyId
    && typeof nextContext.role === 'string'
    && nextContext.role
    && Array.isArray(nextContext.memberships)
    && nextContext.memberships.some(
      (membership) => String(membership?.companyId || '') === activeCompanyId,
    ),
  );
  if (!contextComplete) {
    return {
      activated: false,
      stale: false,
      user: currentUser,
      errorCode: 'tenant_context_verification_failed',
    };
  }
  const candidateUser = {
    ...currentUser,
    activeCompany: nextContext.activeCompany,
    role: nextContext.role,
    memberships: nextContext.memberships,
    tenant: nextContext.tenant,
    productPolicy: null,
  };
  try {
    const verification = await verifyTenantContextPolicy({
      user: candidateUser,
      activeCompanyId,
      requestId,
      isCurrentRequest,
      requestPolicy,
      ...(persist ? { persist } : {}),
    });
    if (verification.stale) {
      return { activated: false, stale: true, user: null, errorCode: null };
    }
    return {
      activated: true,
      stale: false,
      user: verification.user,
      errorCode: null,
    };
  } catch {
    if (!isCurrentRequest(requestId)) {
      return { activated: false, stale: true, user: null, errorCode: null };
    }
    return {
      activated: false,
      stale: false,
      user: currentUser,
      errorCode: 'tenant_context_verification_failed',
    };
  }
}

function normalizePlatformOnlyUser(response) {
  return {
    ...(response?.user || {}),
    auth: { type: 'supabase' },
    activeCompany: null,
    role: null,
    memberships: [],
    tenant: null,
    platform: response?.platform || { admin: false },
    productPolicy: null,
  };
}

function normalizeSessionUser(response) {
  if (response?.auth?.type === 'supabase') {
    return {
      ...(response.user || {}),
      auth: response.auth,
      activeCompany: response.activeCompany || null,
      role: response.role || null,
      memberships: response.memberships || [],
      tenant: response.tenant || null,
      platform: response.platform || { admin: false },
      productPolicy: null,
    };
  }

  return {
    ...(response?.user || response || {}),
    auth: response?.auth || { type: 'legacy' },
  };
}

export function initialSessionState({ hasToken, cached } = {}) {
  return {
    status: 'loading',
    user: hasToken ? (cached || null) : null,
    verifying: Boolean(hasToken),
  };
}

export function sessionVerificationFailureState({ cached, err } = {}) {
  return {
    status: 'error',
    user: null,
    hadCachedUser: Boolean(cached),
    errorStatus: Number(err?.status) || 0,
    errorCode: typeof err?.code === 'string' ? err.code : null,
  };
}

export function useSession() {
  const cached = storedUser();
  const hasToken = !!getToken();

  const [session, setSession] = useState(() => initialSessionState({ hasToken, cached }));
  const tenantContextRequest = useRef(0);

  async function setActiveTenantContext(nextContext = {}, { isCurrent = () => true } = {}) {
    const currentUser = session.user;
    if (!currentUser) return false;
    const requestId = tenantContextRequest.current + 1;
    tenantContextRequest.current = requestId;
    const activation = await resolveTenantContextActivation({
      currentUser,
      nextContext,
      requestId,
      isCurrentRequest: (candidate) => candidate === tenantContextRequest.current && isCurrent(),
    });
    if (activation.stale || !isCurrent()) return false;
    if (activation.activated) {
      setSession((current) => {
        if (requestId !== tenantContextRequest.current || !isCurrent()) return current;
        return {
          ...current,
          status: 'ready',
          user: activation.user,
          tenantContextErrorCode: null,
        };
      });
      return true;
    }
    setSession((current) => ({
      ...current,
      status: 'ready',
      user: currentUser,
      tenantContextErrorCode: activation.errorCode,
    }));
    return false;
  }

  useEffect(() => {
    if (!hasToken) {
      // No token at all — redirect to login immediately without an API call.
      window.location.href = loginHrefForCurrentLocation();
      return;
    }
    let alive = true;
    const requestId = ++tenantContextRequest.current;
    const isCurrent = () => alive && requestId === tenantContextRequest.current;
    const activeCompanyId = companyOnboardingEntry(window.location.search)
      || storedActiveCompanyId() || cached?.activeCompany?.id;
    api.get('/api/me', activeCompanyId ? { headers: { 'x-company-id': activeCompanyId } } : undefined)
      .then(async (r) => {
        if (!isCurrent()) return;
        const selected = await resolveSessionOnboardingContinuation(r, {
          activeCompanyId, allowed: allowsSessionOnboardingContinuation(window.location), isCurrent,
        });
        if (!selected || !isCurrent()) return;
        const baseUser = normalizeSessionUser(selected);
        const [platform, productPolicy] = await Promise.all([
          loadPlatformContext(baseUser),
          loadTenantProductPolicy(baseUser),
        ]);
        if (!isCurrent()) return;
        const user = { ...baseUser, platform, productPolicy };
        try {
          localStorage.setItem('lp_user', JSON.stringify(user));
          if (user.activeCompany?.id) localStorage.setItem(ACTIVE_COMPANY_KEY, user.activeCompany.id);
        } catch {}
        setSession({ status: 'ready', user });
      })
      .catch((err) => {
        if (!isCurrent()) return;
        if (isNoMembershipError(err)) {
          api.get('/api/saas/platform/context')
            .then((platformResponse) => {
              if (!isCurrent()) return;
              const user = normalizePlatformOnlyUser(platformResponse);
              try {
                localStorage.setItem('lp_user', JSON.stringify(user));
                localStorage.removeItem(ACTIVE_COMPANY_KEY);
              } catch {}
              setSession({ status: 'ready', user });
            })
            .catch(async () => {
              if (!isCurrent()) return;
              const handoff = await registrationHandoff({
                error: err,
                hash: window.location.hash,
                request: api.get,
              });
              if (!isCurrent()) return;
              if (handoff) {
                window.location.replace(handoff);
                return;
              }
              if (!isAcceptInviteRoute()) {
                setSession(sessionVerificationFailureState({ cached, err }));
                return;
              }
              setSession({
                status: 'ready',
                user: {
                  auth: { type: 'supabase' },
                  activeCompany: null,
                  role: null,
                  memberships: [],
                  tenant: null,
                  platform: { admin: false },
                  productPolicy: null,
                },
              });
            });
          return;
        }
        // api.js already redirected on 401.
        if (isUnauthorizedError(err)) return;
        setSession(sessionVerificationFailureState({ cached, err }));
      });
    return () => { alive = false; };
  }, []);

  return { ...session, setActiveTenantContext };
}
